/**
 * Voice recording utilities for the IDEA LIVE BUILD chat.
 *
 * Browser MediaRecorder MIME-type support is inconsistent (Safari on iOS ≤ 15 has
 * no MediaRecorder at all; Safari 14.1+/16 supports audio/mp4; Chromium favours
 * audio/webm;codecs=opus; Firefox does audio/ogg;codecs=opus).
 *
 * `pickAudioMimeType()` returns the first supported type from a preference list —
 * we prefer Opus containers first because they compress speech best at low
 * bitrates, then fall back to mp4/aac for Safari/iPhone. The server accepts all
 * of these and stores the exact returned type so playback can match.
 */
export const AUDIO_MIME_PREFERENCES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/ogg",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/aac",
  "audio/mpeg",
];

export const MAX_VOICE_SECONDS = 60;
export const MIN_VOICE_SECONDS = 1;

export function isRecordingSupported() {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function pickAudioMimeType() {
  if (typeof window === "undefined" || !window.MediaRecorder) return "";
  const isSupported = window.MediaRecorder.isTypeSupported;
  if (typeof isSupported !== "function") return "";
  for (const t of AUDIO_MIME_PREFERENCES) {
    try {
      if (isSupported.call(window.MediaRecorder, t)) return t;
    } catch {
      // ignore and try next
    }
  }
  return "";
}

/** Return a short human string like "0:07" for a duration in seconds. */
export function formatDuration(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}
